import { Action, DetailFieldProps, DetailPanel, FieldType, O, UIPermission, useGetCrumbs } from "../../../../../features/ui"
import { PaymentCategory, PaymentType } from "../../../state/types"
import { RoutePath, useRoutesHooks } from "../../../../../features/routes"
import { define } from '../../../../../utils/typeUtils'
import { TableName } from '../../../../../tables'
import { Labels, useDataTypesHooks, useDescribeHooks } from "../../../../../features/data-types"
import { CompanyUserName, useSystemStateHooks } from "../../../../system"
import { useMemo } from "react"
import { CashFundItemsTable } from "../../cash-fund/cash-fund-items-table/cashFundItemsTable"

export const PaymentTypeDetail = () => {
    const { dataType } = useRoutesHooks().parseUrl()
    const paymentType = define(dataType) as PaymentType

    const { userHasPermission } = useSystemStateHooks()
    const readOnly = !userHasPermission(UIPermission.SAVE_PAYMENT_TYPES) || paymentType.archived
    const { formatValue, formatDateTime } = useDataTypesHooks()
    const { describe } = useDescribeHooks()

    const title = describe(TableName.PAYMENT_TYPES, paymentType)
    const crumbs = useGetCrumbs(
        'Medios de Pago',
        RoutePath.PAYMENT_TYPES,
        paymentType.name
    )

    const data = useMemo(() => {
        const { cashFundIds, ...paymentTypeProps } = paymentType
        return paymentTypeProps
    }, [paymentType])

    const cashFundsData = useMemo(() => paymentType.cashFundIds.map(cashFundId => ({ cashFundId })), [paymentType.cashFundIds])

    const fields: DetailFieldProps[] = [
        {
            name: 'name',
            type: FieldType.TEXT,
            label: 'Nombre'
        },
        {
            name: 'category',
            type: FieldType.TEXT,
            label: 'Tipo',
            render: () => <>{Labels.paymentCategoryLabel(paymentType.category)}</>
        },
        {
            name: 'disabled',
            type: FieldType.TEXT,
            label: 'Habilitado',
            render: () => <>{paymentType.disabled ? 'No' : 'Sí'}</>
        },
        {
            name: 'yield',
            type: FieldType.PERCENTAGE,
            label: 'Ajuste',
            render: () => {
                const absYield = Math.abs(paymentType.yield)
                return <>{formatValue(absYield, FieldType.NUMBER)}% de {paymentType.yield <= 0 ? 'Descuento' : 'Recargo'}</>
            }
        },
        {
            name: 'financialCost',
            type: FieldType.PERCENTAGE,
            label: 'Costo Financiero'
        },
        {
            name: 'accreditation',
            type: FieldType.TEXT,
            label: 'Plazo de acreditación',
            render: () => <>{paymentType.accreditationDays === 0 ? 'Inmediato' : (
                `${paymentType.accreditationDays} día${paymentType.accreditationDays === 1 ? '' : 's'}`
            )}</>
        }
    ]
    if (cashFundsData.length > 0) {
        const cashFundsField = {
            name: 'cashFunds',
            type: FieldType.TABLE,
            table: TableName.CASH_FUNDS,
            render: () => (
                <CashFundItemsTable
                    rows={cashFundsData}
                    readOnly
                />
            )
        }
        fields.push(cashFundsField)
    }

    if (paymentType.negativeStateUserId) {
        fields.push({
            name: 'negativeStateUserId',
            type: FieldType.TEXT,
            label: 'Archivado por',
            render: () => {
                const dateStr = paymentType.negativeStateAt ? ` - ${formatDateTime(new Date(paymentType.negativeStateAt))}` : ''
                return <><CompanyUserName userId={paymentType.negativeStateUserId!} />{dateStr}</>
            }
        })
    }

    const actions: O<Action[]> = paymentType.category === PaymentCategory.CREDIT_ACCOUNT ? [] : undefined

    return (
        <DetailPanel
            tableName={TableName.PAYMENT_TYPES}
            title={title}
            crumbs={crumbs}
            fields={fields}
            data={data}
            icon='credit-card'
            readOnly={readOnly}
            actions={actions}
        />
    )
}
